import React from 'react';
import ChevronIcon from 'app/assets/svgIcons/ChevronIcon';
import styles from '../SlideOutPanel.module.scss';
import {
  fetchFeaturesByIssue,
  reorderReleaseFeatures
} from '../../../services/featuresApi';
import { Features, FeatureRow, isUnsavedDraft } from './Features';
import {
  getFeaturesGroupKey,
  mergeReorderIntoGroups
} from './featuresReorderUtils';
import { DISPLAY_LABELS } from './featuresConfig';
import { Loader } from 'app/components/Loader';
import GeneralInfoCommentIcon from '../CommentPanel/GeneralInfoCommentIcon/GeneralCommentIcon';
import { Flex, ToastQueue } from '@adobe/react-spectrum';
import FeaturesSortDropdown, {
  FeatureSortSelection
} from './FeaturesSortDropdown';
import { ProductTitleWithLink } from './ProductTitleWithLink';
import { useActiveAlertIssues } from '../../AlertsAndNotification/hooks/useActiveAlertIssues';
import { mapFieldRefToSelector } from '../../AlertsAndNotification/utils/fieldMapper';
import {
  resolveAlertScrollWinner,
  PhaseOrderEntry
} from '../../AlertsAndNotification/utils/alertScrollPriority';

interface FeaturesContainerProps {
  issueId?: string;
  momentName?: string; // Release/Moment display name
  defaultSurfaces?: string[];
  targetFeatureId?: string | null;
  targetProductId?: string | null;
  onCommentIconClick?: () => void;
  forceExpanded?: boolean; // ✅ NEW: Allow parent to force accordion open
  printMode?: boolean; // Expand all feature groups for browser PDF export
  onDataRefresh?: (patchResponse?: unknown) => void; // ✅ NEW: Callback to refresh slideout header data
  onNotificationRefresh?: (patchResponse?: unknown) => void; // Callback to refresh notifications after successful feature PATCH
  readonly?: boolean; // ✅ NEW: Make features section read-only (no editing/interactions)
  hideCommentIcon?: boolean; // ✅ NEW: Hide comment icon next to Features title
  /**
   * Whether the parent slideout is currently open. Used only to scroll the first alerted
   * feature row into view exactly once per slideout-open (mirrors useFieldHighlight's
   * isOpen-transition-reset pattern) — not required for the existing highlight/auto-expand
   * logic below, which is driven purely by alertedFeatureIds.
   */
  isOpen?: boolean;
  /**
   * Phase order (display order, [] for non-phased moments) — passed straight through to
   * {@link resolveAlertScrollWinner} so this container only scrolls when Features actually wins
   * the cross-section priority contest (see alertScrollPriority.ts), not merely because a
   * feature alert exists somewhere.
   */
  phases?: PhaseOrderEntry[];
}

export interface FeaturesContainerRef {
  refresh: () => void;
  expand: () => void;
}

const REORDER_DEBOUNCE_MS = 600;

const FeaturesContainer = React.forwardRef<
  FeaturesContainerRef,
  FeaturesContainerProps
>(
  (
    {
      issueId,
      momentName,
      defaultSurfaces,
      targetFeatureId,
      targetProductId,
      onCommentIconClick,
      forceExpanded = false, // ✅ NEW: Default to false
      printMode = false,
      onDataRefresh, // ✅ NEW: Callback to refresh slideout header data
      onNotificationRefresh, // Callback to refresh notifications after successful feature PATCH
      readonly = false, // ✅ NEW: Default to false (editable)
      hideCommentIcon = false, // ✅ NEW: Default to false (show comment icon)
      isOpen,
      phases = []
    },
    ref
  ) => {
    // Initialize state from localStorage, default to true (expanded)
    const [isExpanded, setIsExpanded] = React.useState(() => {
      const stored = localStorage.getItem('slideout_features_expanded');
      return stored !== null ? stored === 'true' : true;
    });

    // ✅ NEW: Force expand when parent requests it
    React.useEffect(() => {
      if (forceExpanded || printMode) {
        setIsExpanded(true);
        if (forceExpanded) {
          // User navigation preference; print mode must not alter preferences.
          localStorage.setItem('slideout_features_expanded', 'true');
        }
      }
    }, [forceExpanded, printMode]);

    // Active alert issues (data-quality-alert) for this moment — used below to auto-expand the
    // Features accordion / product group containing an alerted feature, so the declarative
    // highlight applied in Features.tsx is actually visible (mirrors forceExpanded above, just
    // triggered by an alert instead of a feature-card click).
    const activeFeatureAlertIssues = useActiveAlertIssues(issueId);
    const alertedFeatureIds = React.useMemo(() => {
      const ids = new Set<string>();
      activeFeatureAlertIssues.forEach(issue => {
        const mapping = mapFieldRefToSelector(issue.fieldRef, issue.ruleId);
        if (mapping?.section === 'features' && mapping.featureId) {
          ids.add(mapping.featureId);
        }
      });
      return ids;
    }, [activeFeatureAlertIssues]);

    React.useEffect(() => {
      if (alertedFeatureIds.size > 0) {
        setIsExpanded(true);
        localStorage.setItem('slideout_features_expanded', 'true');
      }
    }, [alertedFeatureIds]);

    const [groups, setGroups] = React.useState<any[] | null>(null);

    // First alerted feature id in the same display order rows are rendered below (groups →
    // rows), so the scroll target below matches whatever order is visible on screen — mirrors
    // the `id` derivation used when building `rows` in the render loop further down
    // (f.featureId || f.id || `${gIdx}-${idx}`), rather than deriving order from
    // activeFeatureAlertIssues (Redux issue order), which need not match row display order.
    const firstAlertedFeatureId = React.useMemo(() => {
      if (alertedFeatureIds.size === 0 || !groups) return null;
      for (let gIdx = 0; gIdx < groups.length; gIdx++) {
        const features = groups[gIdx]?.feature || groups[gIdx]?.features || [];
        for (let idx = 0; idx < features.length; idx++) {
          const f = features[idx];
          const id = f.featureId || f.id || `${gIdx}-${idx}`;
          if (alertedFeatureIds.has(id)) return id as string;
        }
      }
      return null;
    }, [groups, alertedFeatureIds]);

    // Cross-section scroll priority winner (date/general-info, milestones, features — whichever
    // is visually topmost). Features may only scroll if THEY actually won the contest, not merely
    // because a feature alert exists (see alertScrollPriority.ts).
    const scrollPriorityWinner = React.useMemo(
      () => resolveAlertScrollWinner(activeFeatureAlertIssues, phases),
      [activeFeatureAlertIssues, phases]
    );
    const featuresWonScrollPriority =
      scrollPriorityWinner?.mapping.section === 'features';

    // Kept fresh every render (no dep array) so the scroll-attempt effect below — which
    // intentionally only re-runs on `isOpen` transitions, see comment there — can read
    // up-to-date values during its 800ms/2000ms retry window without re-arming on every alert
    // change while the slideout stays open.
    const firstAlertedFeatureIdRef = React.useRef(firstAlertedFeatureId);
    const featuresWonScrollPriorityRef = React.useRef(
      featuresWonScrollPriority
    );
    React.useEffect(() => {
      firstAlertedFeatureIdRef.current = firstAlertedFeatureId;
      featuresWonScrollPriorityRef.current = featuresWonScrollPriority;
    });

    /**
     * Scroll the first alerted feature row into view — at most once per slideout-open (or per
     * fresh data load for this issueId), same "shouldScroll only on first application" semantics
     * as `useFieldHighlight.ts`'s `hasAppliedHighlightsRef`/isOpen-transition reset (see
     * useFieldHighlight.ts:30-43). This is the declarative-side counterpart to that hook's
     * imperative scroll, which explicitly skips features/milestones rows (see
     * useFieldHighlight.ts:116-121) because they're rendered dynamically from fetched data.
     *
     * Deliberately keyed on `[isOpen]` only (not `firstAlertedFeatureId`/`featuresWonScrollPriority`):
     * the scroll decision is made once, within the 800ms/2000ms window right after the slideout
     * opens, and is never re-armed by a later change in which section wins the priority contest
     * (e.g. the user resolves a higher-priority alert while the panel stays open, making Features
     * newly "topmost" — that should NOT trigger a fresh scroll; scrolling is only expected on
     * open/refresh).
     */
    const hasScrolledToAlertedFeatureRef = React.useRef(false);
    const previousIsOpenForScrollRef = React.useRef(false);

    React.useEffect(() => {
      if (!isOpen && previousIsOpenForScrollRef.current) {
        hasScrolledToAlertedFeatureRef.current = false;
      }
      previousIsOpenForScrollRef.current = !!isOpen;
    }, [isOpen]);

    React.useEffect(() => {
      if (!isOpen) return;
      if (hasScrolledToAlertedFeatureRef.current) return;

      const attemptScroll = () => {
        if (hasScrolledToAlertedFeatureRef.current) return;
        if (
          !firstAlertedFeatureIdRef.current ||
          !featuresWonScrollPriorityRef.current
        ) {
          return;
        }
        const rowEl = document.querySelector(
          `[data-testid="features-row-${firstAlertedFeatureIdRef.current}"]`
        ) as HTMLElement | null;
        if (rowEl) {
          rowEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
          hasScrolledToAlertedFeatureRef.current = true;
        }
      };

      // Two-attempt retry (same 800ms/2000ms cadence as useFieldHighlight.ts:151-153): the first
      // covers the isExpanded state flip above + Features re-render, the second is a safety net
      // for a slower fetchFeaturesByIssue response.
      const timer1 = setTimeout(attemptScroll, 800);
      const timer2 = setTimeout(() => {
        attemptScroll();
        // Close the open/refresh scroll window regardless of outcome — no further attempts until
        // the next isOpen close→open transition, even if the winner changes afterward.
        hasScrolledToAlertedFeatureRef.current = true;
      }, 2000);
      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
      };
    }, [isOpen]);
    const [isLoading, setIsLoading] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    const [reloadToken, setReloadToken] = React.useState(0);
    const [rerenderKey, setRerenderKey] = React.useState(0);
    const [sortBy, setSortBy] = React.useState<FeatureSortSelection>(null);
    const [customSortEnabled, setCustomSortEnabled] = React.useState(false);
    /**
     * Incremented when reorder API fails so Features can reset `localFeatures` from current
     * `rows` (still server order in `groups`) without remounting.
     */
    const [serverListResyncKeyByGroup, setServerListResyncKeyByGroup] =
      React.useState<Record<string, number>>({});

    React.useEffect(() => {
      setSortBy(null);
      setCustomSortEnabled(false);
    }, [issueId]);

    const pendingReorderRef = React.useRef<Record<string, FeatureRow[]>>({});
    const reorderTimersRef = React.useRef<
      Record<string, ReturnType<typeof setTimeout>>
    >({});

    React.useEffect(() => {
      Object.values(reorderTimersRef.current).forEach(t => clearTimeout(t));
      reorderTimersRef.current = {};
      pendingReorderRef.current = {};
      setServerListResyncKeyByGroup({});
    }, [issueId]);

    React.useEffect(() => {
      return () => {
        Object.values(reorderTimersRef.current).forEach(t => clearTimeout(t));
      };
    }, []);

    // Debug: Check if target props are received
    React.useEffect(() => {
      if (targetFeatureId) {
      }
    }, [targetFeatureId, targetProductId]);

    const toggleExpanded = () => {
      setIsExpanded(prev => {
        const newValue = !prev;
        // Persist to localStorage
        localStorage.setItem('slideout_features_expanded', String(newValue));
        return newValue;
      });
    };

    const refresh = () => setReloadToken(v => v + 1);

    const expand = React.useCallback(() => {
      setIsExpanded(true);
      localStorage.setItem('slideout_features_expanded', 'true');
    }, []);

    // Expose refresh and expand methods to parent via ref
    React.useImperativeHandle(ref, () => ({
      refresh,
      expand
    }));

    React.useEffect(() => {
      if (!issueId) {
        setGroups([]);
        return;
      }
      let cancelled = false;
      setIsLoading(true);
      setError(null);
      fetchFeaturesByIssue(issueId)
        .then(res => {
          if (cancelled) return;
          if (res.success) {
            setGroups(res.data || []);
          } else {
            setError(
              res.message || (res as any).error || 'Failed to load features'
            );
            setGroups([]);
          }
        })
        .catch(err => {
          if (cancelled) return;
          setError(err?.message || 'Failed to load features');
          setGroups([]);
        })
        .finally(() => {
          if (!cancelled) setIsLoading(false);
        });
      return () => {
        cancelled = true;
      };
    }, [issueId, reloadToken]);

    const extractPhaseDate = (phaseData: any): string => {
      if (!phaseData) return 'N/A';
      if (phaseData.date && typeof phaseData.date === 'object') {
        if (phaseData.date.releaseDate) return phaseData.date.releaseDate;
        if (phaseData.date.releaseQuarter) return phaseData.date.releaseQuarter;
        if (phaseData.date.releaseMonth) return phaseData.date.releaseMonth;
        if (phaseData.date.releaseDateStatus)
          return phaseData.date.releaseDateStatus;
        if (phaseData.date.targetStart) return phaseData.date.targetStart;
        if (phaseData.date.targetQuarter) return phaseData.date.targetQuarter;
        if (phaseData.date.targetMonth) return phaseData.date.targetMonth;
        if (phaseData.date.targetDateStatus)
          return phaseData.date.targetDateStatus;
        if (phaseData.date.effectiveDate) return phaseData.date.effectiveDate;
        if (phaseData.date.effectiveDateStatus)
          return phaseData.date.effectiveDateStatus;
      }
      if (phaseData.phaseDate) {
        if (phaseData.phaseDate.months?.[0])
          return phaseData.phaseDate.months[0];
        if (phaseData.phaseDate.date) return phaseData.phaseDate.date;
        if (phaseData.phaseDate.quarters?.[0])
          return phaseData.phaseDate.quarters[0];
      }
      if (phaseData.dates?.releaseDate) return phaseData.dates.releaseDate;
      if (typeof phaseData === 'string') return phaseData;
      return 'N/A';
    };

    const flushReorderPersist = React.useCallback(
      async (groupKey: string) => {
        const orderedFeatures = pendingReorderRef.current[groupKey];
        if (!orderedFeatures?.length) return;
        if (!issueId) {
          ToastQueue.negative(
            'Missing issue context. Could not save feature order.',
            { timeout: 5000 }
          );
          return;
        }
        const persistedOnly = orderedFeatures.filter(
          f => !isUnsavedDraft(f.id)
        );
        if (!persistedOnly.length) {
          delete pendingReorderRef.current[groupKey];
          return;
        }
        delete pendingReorderRef.current[groupKey];
        const body = {
          features: persistedOnly.map((f, i) => ({
            featureId: f.id,
            displayOrder: i + 1
          }))
        };

        const res = await reorderReleaseFeatures(issueId, body);
        if (!res.success) {
          // ToastQueue.negative(res.message || 'Unable to save feature order.', {
          //   timeout: 5000
          // });
          setSortBy(null);
          setCustomSortEnabled(false);
          setServerListResyncKeyByGroup(prev => ({
            ...prev,
            [groupKey]: (prev[groupKey] ?? 0) + 1
          }));
          return;
        }

        setGroups(prev =>
          prev ? mergeReorderIntoGroups(prev, groupKey, persistedOnly) : prev
        );
      },
      [issueId, groups]
    );

    const handleFeaturesReorderCompleted = React.useCallback(
      (groupKey: string, orderedFeatures: FeatureRow[]) => {
        setCustomSortEnabled(true);
        setSortBy('custom');
        pendingReorderRef.current[groupKey] = orderedFeatures;
        const existing = reorderTimersRef.current[groupKey];
        if (existing) clearTimeout(existing);
        reorderTimersRef.current[groupKey] = setTimeout(() => {
          delete reorderTimersRef.current[groupKey];
          void flushReorderPersist(groupKey);
        }, REORDER_DEBOUNCE_MS);
      },
      [flushReorderPersist]
    );

    return (
      <div className={styles.section} data-testid="features-section-container">
        <div
          className={styles.sectionHeader}
          style={{ cursor: 'pointer' }}
          data-testid="features-section-toggle-header"
        >
          <div
            style={{ display: 'flex', alignItems: 'center', gap: 0 }}
            onClick={toggleExpanded}
          >
            <div className={styles.phaseToggle}>
              <ChevronIcon isExpanded={isExpanded} />
            </div>
            <Flex direction="row" alignItems="center">
              <span className={styles.sectionTitle}>Features</span>
              {!hideCommentIcon && (
                <GeneralInfoCommentIcon
                  phaseName={'Features'}
                  onClick={e => {
                    e?.stopPropagation();
                    onCommentIconClick?.();
                  }}
                />
              )}
            </Flex>
          </div>
          {isExpanded && (
            <div className={styles.featuresSortWrapper}>
              <FeaturesSortDropdown
                value={sortBy}
                onChange={setSortBy}
                isCustomEnabled={customSortEnabled}
                disabled={!isExpanded}
              />
            </div>
          )}
        </div>
        {isExpanded && (
          <div
            className={styles.sectionContent}
            data-testid="features-section-content"
          >
            <div style={{ position: 'relative' }}>
              {error ? (
                <div>{error}</div>
              ) : (
                <>
                  {(groups || []).map((group: any, gIdx: number) => {
                    const rows: FeatureRow[] = (
                      group?.feature ||
                      group?.features ||
                      []
                    ).map(
                      (f: any, idx: number): FeatureRow => ({
                        id: f.featureId || f.id || `${gIdx}-${idx}`,
                        name: f.name || 'Untitled',
                        jiraLink: f.nameLink || f.ppc || '',
                        // Prefer explicit riskLevel from API; do NOT override with status
                        riskLevel: f.riskLevel || f.risk || f.status,
                        status: f.status,
                        demo: {
                          url: f.demo?.link,
                          etaDate: f.demo?.date,
                          available: undefined
                        },
                        prd: {
                          url: f.prd?.link,
                          etaDate: f.prd?.date,
                          available: undefined
                        },
                        internalBeta: extractPhaseDate(f.internalBeta),
                        privateBeta: extractPhaseDate(f.privateBeta),
                        publicBeta: extractPhaseDate(f.publicBeta),
                        ga: extractPhaseDate(f.ga),
                        surfaces: Array.isArray(f.surfaces)
                          ? f.surfaces
                          : undefined,
                        mvp: !!f.mvp,
                        // Map new boolean flags from API
                        genAi: !!(f.genai ?? f.genAi),
                        consumesCredit: !!(
                          f.consumesCredit ??
                          f.premiumCredit ??
                          f.premium_credit
                        )
                      })
                    );

                    // (debug logging removed)
                    // ✅ FIX: Use only product name for groupKey (not index) so session storage keys remain stable
                    // when product order changes after adding new products
                    const groupKey = `${group?.productName || `group-${gIdx}`}`;
                    const collapsedKey = `features:group:collapsed:${groupKey}`;

                    // Auto-expand this accordion if it matches the target product OR if it contains the target feature
                    const containsTargetFeature =
                      targetFeatureId &&
                      rows.some(r => r.id === targetFeatureId);
                    const isTargetProduct =
                      targetProductId && group?.productName === targetProductId;
                    // ...or if it contains a feature currently flagged by an active alert.
                    const containsAlertedFeature =
                      alertedFeatureIds.size > 0 &&
                      rows.some(r => alertedFeatureIds.has(r.id));
                    const shouldExpand =
                      containsTargetFeature ||
                      isTargetProduct ||
                      containsAlertedFeature;
                    const isCollapsed =
                      printMode || shouldExpand
                        ? false
                        : localStorage.getItem(collapsedKey) === 'true';

                    // Debug: Log when passing highlightedFeatureId
                    if (targetFeatureId) {
                    }

                    return (
                      <div
                        key={`feat-group-${groupKey}`}
                        style={{ paddingLeft: 24 }}
                        data-testid={`features-group-container-${groupKey}`}
                      >
                        <div
                          onClick={() => {
                            const current =
                              localStorage.getItem(collapsedKey) === 'true';
                            localStorage.setItem(
                              collapsedKey,
                              String(!current)
                            );
                            setRerenderKey(v => v + 1);
                          }}
                          style={{
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            padding: '6px 0'
                          }}
                          className={styles.featuresGroupHeader}
                          data-testid={`features-group-header-${groupKey}`}
                        >
                          <div
                            className={styles.phaseToggle}
                            data-testid={`features-group-chevron-${groupKey}`}
                          >
                            <ChevronIcon isExpanded={!isCollapsed} />
                          </div>
                          <div
                            className={styles.featuresProductTitle}
                            data-testid={`features-group-title-${groupKey}`}
                          >
                            <ProductTitleWithLink
                              productName={group?.productName || 'Product'}
                              readonly={readonly}
                            />
                          </div>
                        </div>
                        {!isCollapsed && (
                          <div
                            className={styles.sectionContent}
                            data-testid={`features-group-content-${groupKey}`}
                          >
                            <Features
                              key={groupKey}
                              rows={rows}
                              serverListResyncKey={
                                serverListResyncKeyByGroup[groupKey] ?? 0
                              }
                              groupKey={groupKey}
                              releaseId={issueId}
                              releaseName={momentName}
                              productId={group?.productName}
                              defaultSurfaces={defaultSurfaces}
                              onRefreshFeatures={patchResponse => {
                                refresh(); // Refresh features list
                                onDataRefresh?.(patchResponse);
                                onNotificationRefresh?.(patchResponse);
                              }}
                              highlightedFeatureId={
                                containsTargetFeature ? targetFeatureId : null
                              }
                              sortBy={sortBy}
                              onReorderCompleted={ordered =>
                                handleFeaturesReorderCompleted(
                                  groupKey,
                                  ordered
                                )
                              }
                              readonly={readonly}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {groups && groups.length === 0 && (
                    <div>{DISPLAY_LABELS.noFeatures}</div>
                  )}
                </>
              )}
              {isLoading && (
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'rgba(255,255,255,0.6)',
                    zIndex: 2
                  }}
                  data-testid="features-loading-overlay"
                >
                  <Loader />
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }
);

FeaturesContainer.displayName = 'FeaturesContainer';

export default FeaturesContainer;
